import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor as useTipTapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FC, useEffect, useRef } from 'react';
import { LangEnum, LangType, getDefaultLang } from '../../helpers/lang';
import { useClickOutside } from '../../hooks/use-click-outside';
import { useLang } from '../../hooks/use-lang';
import { useToggle } from '../../hooks/use-toggle';
import { useInputLangStore } from '../../store/use-input-lang-store';
import s from './editor.module.css';
import { TipTapToolbar } from './toolbar/toolbar';

const isMultiLang = (value?: LangType | string) =>
  typeof value === 'object' || typeof value === 'undefined';
const getLocalValue = (value?: LangType | string, language?: LangEnum) => {
  return isMultiLang(value) ? value?.[language || getDefaultLang()] : value;
};

export interface Props {
  onChange?: (data: LangType | string) => void;
  value?: LangType | string;
  label?: string;
  required?: boolean;
}

export const Editor: FC<Props> = (props) => {
  const { value, onChange } = props;
  const { language } = useInputLangStore();
  const { t } = useLang();

  const extensions = [
    StarterKit,
    TextStyle,
    Color,
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Placeholder.configure({
      placeholder: '',
    }),
  ];

  const editor = useTipTapEditor({
    extensions,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      const result = isMultiLang(value) ? { ...value, [language]: content } : content;
      onChange?.(result);
    },
  });

  useEffect(() => {
    const placeholderExtension = editor?.extensionManager.extensions.find(
      (e) => e.name === 'placeholder',
    );

    if (placeholderExtension) {
      placeholderExtension.options.placeholder = t('Введите описание акции');
      editor?.view.dispatch(editor.state.tr);
    }
  }, [t]);

  const defaultValue = getLocalValue(value, language);

  useEffect(() => {
    if (editor?.isEmpty && defaultValue) {
      editor?.commands.setContent(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    editor?.commands.setContent(defaultValue || '');
  }, [language]);

  const ref = useRef<HTMLDivElement>(null);
  const [isFocused, , setIsFocused] = useToggle(false);

  useClickOutside(ref, () => setIsFocused(false));

  return (
    <div className={s.component} ref={ref} onFocus={() => setIsFocused(true)}>
      <TipTapToolbar editor={editor} isFocused={isFocused} />
      <EditorContent editor={editor} className={s.editor} data-testid="editor" />
      {/* <BubbleMenu editor={editor}>This is the bubble menu</BubbleMenu> */}
    </div>
  );
};
